import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ewmAPI, materialManagementAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const EWMAssignments = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('ewm_assignments');
  const [activeTab, setActiveTab] = useState('wh-plant-sl');
  const [warehouses, setWarehouses] = useState([]);
  const [plants, setPlants] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [storageTypes, setStorageTypes] = useState([]);
  const [storageSections, setStorageSections] = useState([]);
  const [storageBins, setStorageBins] = useState([]);
  
  const [whPlantSlAssignments, setWhPlantSlAssignments] = useState([]);
  const [typeWhAssignments, setTypeWhAssignments] = useState([]);
  const [sectionTypeAssignments, setSectionTypeAssignments] = useState([]);
  const [binTypeAssignments, setBinTypeAssignments] = useState([]);
  const [binSectionAssignments, setBinSectionAssignments] = useState([]);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Form state
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedPlant, setSelectedPlant] = useState('');
  const [selectedStorageLocation, setSelectedStorageLocation] = useState('');
  const [selectedStorageType, setSelectedStorageType] = useState('');
  const [selectedStorageSection, setSelectedStorageSection] = useState('');
  const [selectedStorageBin, setSelectedStorageBin] = useState('');
  const [filteredStorageLocations, setFilteredStorageLocations] = useState([]);
  const [filteredStorageSections, setFilteredStorageSections] = useState([]);

  const tabs = [
    { id: 'wh-plant-sl', label: 'Warehouse → Plant + Storage Location' },
    { id: 'type-wh', label: 'Storage Type → Warehouse' },
    { id: 'section-type', label: 'Storage Section → Storage Type' },
    { id: 'bin-type', label: 'Storage Bin → Storage Type' },
    { id: 'bin-section', label: 'Storage Bin → Storage Section' },
  ];

  useEffect(() => {
    loadMasterData();
    loadAssignments();
  }, []);

  // Filter storage locations when plant changes
  useEffect(() => {
    if (selectedPlant) {
      const filtered = storageLocations.filter(sl => sl.plant_id === selectedPlant);
      setFilteredStorageLocations(filtered);
      setSelectedStorageLocation(''); // Reset selection
    } else {
      setFilteredStorageLocations([]);
    }
  }, [selectedPlant, storageLocations]);

  const loadMasterData = async () => {
    try {
      const [warehousesRes, plantsRes, storageLocationsRes, storageTypesRes, storageSectionsRes, storageBinsRes] = await Promise.all([
        ewmAPI.getWarehouses(),
        materialManagementAPI.getPlants(),
        materialManagementAPI.getStorageLocations(),
        ewmAPI.getStorageTypes(),
        ewmAPI.getStorageSections(),
        ewmAPI.getStorageBins(),
      ]);
      setWarehouses(warehousesRes.data.filter(w => w.status === 'Active'));
      setPlants(plantsRes.data); // Plants don't have status field
      setStorageLocations(storageLocationsRes.data.filter(sl => sl.status === 'Active'));
      setStorageTypes(storageTypesRes.data.filter(st => st.status === 'Active'));
      setStorageSections(storageSectionsRes.data.filter(ss => ss.status === 'Active'));
      setStorageBins(storageBinsRes.data.filter(sb => sb.status === 'Active'));
    } catch (error) {
      console.error('Failed to load master data:', error);
      toast.error('Failed to load master data');
    }
  };

  const loadAssignments = async () => {
    try {
      const whPlantSlRes = await ewmAPI.getWarehousePlantStorageLocationAssignments();
      setWhPlantSlAssignments(whPlantSlRes.data);
      
      // Load storage types to check warehouse assignments
      const typesRes = await ewmAPI.getStorageTypes();
      const assignedTypes = typesRes.data.filter(t => t.warehouse_number);
      setTypeWhAssignments(assignedTypes);
      
      // Load storage sections to check type assignments
      const sectionsRes = await ewmAPI.getStorageSections();
      const assignedSections = sectionsRes.data.filter(s => s.storage_type_id);
      setSectionTypeAssignments(assignedSections);
      
      // Load storage bins to check type and section assignments
      const binsRes = await ewmAPI.getStorageBins();
      const binsWithType = binsRes.data.filter(b => b.storage_type_id);
      const binsWithSection = binsRes.data.filter(b => b.storage_section_id);
      setBinTypeAssignments(binsWithType);
      setBinSectionAssignments(binsWithSection);
    } catch (error) {
      console.error('Failed to load assignments:', error);
      toast.error('Failed to load assignments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (activeTab === 'wh-plant-sl') {
        const payload = {
          warehouse_number: selectedWarehouse,
          plant_id: selectedPlant,
          storage_location_id: selectedStorageLocation,
        };
        await ewmAPI.createWarehousePlantStorageLocationAssignment(payload);
        toast.success('Warehouse to Plant + Storage Location assignment created');
      } else if (activeTab === 'type-wh') {
        await ewmAPI.assignStorageTypeToWarehouse(selectedStorageType, selectedWarehouse);
        toast.success('Storage Type assigned to Warehouse');
      } else if (activeTab === 'section-type') {
        await ewmAPI.assignStorageSectionToType(selectedStorageSection, selectedStorageType);
        toast.success('Storage Section assigned to Storage Type');
      } else if (activeTab === 'bin-type') {
        await ewmAPI.assignStorageBinToType(selectedStorageBin, selectedStorageType);
        toast.success('Storage Bin assigned to Storage Type');
      } else if (activeTab === 'bin-section') {
        await ewmAPI.assignStorageBinToSection(selectedStorageBin, selectedStorageSection);
        toast.success('Storage Bin assigned to Storage Section');
      }
      
      setIsDialogOpen(false);
      setSelectedWarehouse('');
      setSelectedPlant('');
      setSelectedStorageLocation('');
      setSelectedStorageType('');
      setSelectedStorageSection('');
      setSelectedStorageBin('');
      loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment creation failed');
    }
  };

  const handleDelete = async (assignment) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      if (activeTab === 'wh-plant-sl') {
        await ewmAPI.deleteWarehousePlantStorageLocationAssignment(
          assignment.warehouse_number,
          assignment.plant_id,
          assignment.storage_location_id
        );
      } else if (activeTab === 'type-wh') {
        await ewmAPI.unassignStorageTypeFromWarehouse(assignment.storage_type_id);
      } else if (activeTab === 'section-type') {
        await ewmAPI.unassignStorageSectionFromType(assignment.storage_section_id);
      } else if (activeTab === 'bin-type') {
        await ewmAPI.unassignStorageBinFromType(assignment.storage_bin_id);
      } else if (activeTab === 'bin-section') {
        await ewmAPI.unassignStorageBinFromSection(assignment.storage_bin_id);
      }
      toast.success('Assignment deleted successfully');
      loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getWarehouseName = (warehouseNumber) => {
    const warehouse = warehouses.find(w => w.warehouse_number === warehouseNumber);
    return warehouse ? warehouse.name : warehouseNumber;
  };

  const getPlantName = (plantId) => {
    const plant = plants.find(p => p.plant_id === plantId);
    return plant ? plant.plant_name : plantId;
  };

  const getStorageLocationName = (plantId, storageLocationId) => {
    const sl = storageLocations.find(
      sl => sl.plant_id === plantId && sl.storage_location_id === storageLocationId
    );
    return sl ? sl.name : storageLocationId;
  };

  const getStorageTypeName = (storageTypeId) => {
    const type = storageTypes.find(t => t.storage_type_id === storageTypeId);
    return type ? type.name : storageTypeId;
  };

  const getStorageSectionName = (storageSectionId) => {
    const section = storageSections.find(s => s.storage_section_id === storageSectionId);
    return section ? section.name : storageSectionId;
  };

  const getStorageBinName = (storageBinId) => {
    const bin = storageBins.find(b => b.storage_bin_id === storageBinId);
    return bin ? bin.name : storageBinId;
  };

  const getCurrentAssignments = () => {
    switch (activeTab) {
      case 'wh-plant-sl': return whPlantSlAssignments;
      case 'type-wh': return typeWhAssignments;
      case 'section-type': return sectionTypeAssignments;
      case 'bin-type': return binTypeAssignments;
      case 'bin-section': return binSectionAssignments;
      default: return [];
    }
  };

  const getUnassignedEntities = () => {
    switch (activeTab) {
      case 'type-wh':
        return storageTypes.filter(t => !t.warehouse_number);
      case 'section-type':
        return storageSections.filter(s => !s.storage_type_id);
      case 'bin-type':
        return storageBins.filter(b => !b.storage_type_id);
      case 'bin-section':
        return storageBins.filter(b => !b.storage_section_id);
      default:
        return [];
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">EWM Assignments</h1>
          <p className="text-gray-600 mt-2">
            Manage all warehouse management entity assignments
          </p>
        </div>

        {/* Assignment Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Assignment Content */}
        <Card className="p-6 mb-6">
          <div className="mb-4">
            {activeTab === 'wh-plant-sl' && (
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                <h3 className="font-semibold text-orange-900 mb-2">
                  Warehouse → Plant + Storage Location Assignment
                </h3>
                <p className="text-sm text-orange-800">
                  <strong>Business Rule (M:N):</strong> A warehouse can manage several storage locations.
                  Each (Plant + Storage Location) combination can be assigned to only ONE warehouse.
                </p>
              </div>
            )}
            
            {activeTab === 'type-wh' && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">
                  Storage Type → Warehouse Assignment
                </h3>
                <p className="text-sm text-green-800">
                  <strong>Business Rule (1:M):</strong> Each Storage Type must be assigned to exactly ONE Warehouse.
                  A warehouse can have multiple storage types.
                </p>
              </div>
            )}
            
            {activeTab === 'section-type' && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">
                  Storage Section → Storage Type Assignment
                </h3>
                <p className="text-sm text-green-800">
                  <strong>Business Rule (1:M):</strong> Each Storage Section must be assigned to exactly ONE Storage Type.
                  A storage type can have multiple sections.
                </p>
              </div>
            )}
            
            {activeTab === 'bin-type' && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">
                  Storage Bin → Storage Type Assignment
                </h3>
                <p className="text-sm text-green-800">
                  <strong>Business Rule (1:M):</strong> Each Storage Bin must be assigned to exactly ONE Storage Type.
                  A storage type can have multiple bins.
                </p>
              </div>
            )}
            
            {activeTab === 'bin-section' && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <h3 className="font-semibold text-green-900 mb-2">
                  Storage Bin → Storage Section Assignment
                </h3>
                <p className="text-sm text-green-800">
                  <strong>Business Rule (1:M, Optional):</strong> Each Storage Bin can optionally be assigned to ONE Storage Section.
                  A storage section can have multiple bins.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Active Assignments</h2>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                {writable && (<Button 
                  type="button"
                  onClick={() => {
                    setSelectedWarehouse('');
                    setSelectedPlant('');
                    setSelectedStorageLocation('');
                    setSelectedStorageType('');
                    setSelectedStorageSection('');
                    setSelectedStorageBin('');
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Assignment
                </Button>)}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {activeTab === 'wh-plant-sl' && 'Create Warehouse Assignment'}
                    {activeTab === 'type-wh' && 'Assign Storage Type to Warehouse'}
                    {activeTab === 'section-type' && 'Assign Storage Section to Storage Type'}
                    {activeTab === 'bin-type' && 'Assign Storage Bin to Storage Type'}
                    {activeTab === 'bin-section' && 'Assign Storage Bin to Storage Section'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Tab 1: Warehouse → Plant + Storage Location */}
                  {activeTab === 'wh-plant-sl' && (
                    <>
                      <div>
                        <Label htmlFor="warehouse_number">Warehouse *</Label>
                        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Warehouse" />
                          </SelectTrigger>
                          <SelectContent>
                            {warehouses.map(warehouse => (
                              <SelectItem key={warehouse.warehouse_number} value={warehouse.warehouse_number}>
                                {warehouse.warehouse_number} - {warehouse.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="plant_id">Plant *</Label>
                        <Select value={selectedPlant} onValueChange={setSelectedPlant} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Plant" />
                          </SelectTrigger>
                          <SelectContent>
                            {plants.map(plant => (
                              <SelectItem key={plant.plant_id} value={plant.plant_id}>
                                {plant.plant_id} - {plant.plant_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="storage_location_id">Storage Location *</Label>
                        <Select 
                          value={selectedStorageLocation} 
                          onValueChange={setSelectedStorageLocation} 
                          required
                          disabled={!selectedPlant}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={selectedPlant ? "Select Storage Location" : "Select Plant first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredStorageLocations.map(sl => (
                              <SelectItem key={sl.storage_location_id} value={sl.storage_location_id}>
                                {sl.storage_location_id} - {sl.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!selectedPlant && (
                          <p className="text-xs text-gray-500 mt-1">Please select a plant first</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Tab 2: Storage Type → Warehouse */}
                  {activeTab === 'type-wh' && (
                    <>
                      <div>
                        <Label htmlFor="storage_type_id">Storage Type *</Label>
                        <Select value={selectedStorageType} onValueChange={setSelectedStorageType} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageTypes.filter(t => !t.warehouse_number).map(type => (
                              <SelectItem key={type.storage_type_id} value={type.storage_type_id}>
                                {type.storage_type_id} - {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {storageTypes.filter(t => !t.warehouse_number).length === 0 && (
                          <p className="text-xs text-gray-500 mt-1">All storage types are already assigned</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="warehouse_number">Warehouse *</Label>
                        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Warehouse" />
                          </SelectTrigger>
                          <SelectContent>
                            {warehouses.map(warehouse => (
                              <SelectItem key={warehouse.warehouse_number} value={warehouse.warehouse_number}>
                                {warehouse.warehouse_number} - {warehouse.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Tab 3: Storage Section → Storage Type */}
                  {activeTab === 'section-type' && (
                    <>
                      <div>
                        <Label htmlFor="storage_section_id">Storage Section *</Label>
                        <Select value={selectedStorageSection} onValueChange={setSelectedStorageSection} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Section" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageSections.filter(s => !s.storage_type_id).map(section => (
                              <SelectItem key={section.storage_section_id} value={section.storage_section_id}>
                                {section.storage_section_id} - {section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {storageSections.filter(s => !s.storage_type_id).length === 0 && (
                          <p className="text-xs text-gray-500 mt-1">All storage sections are already assigned</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="storage_type_id">Storage Type *</Label>
                        <Select value={selectedStorageType} onValueChange={setSelectedStorageType} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageTypes.map(type => (
                              <SelectItem key={type.storage_type_id} value={type.storage_type_id}>
                                {type.storage_type_id} - {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Tab 4: Storage Bin → Storage Type */}
                  {activeTab === 'bin-type' && (
                    <>
                      <div>
                        <Label htmlFor="storage_bin_id">Storage Bin *</Label>
                        <Select value={selectedStorageBin} onValueChange={setSelectedStorageBin} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Bin" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageBins.filter(b => !b.storage_type_id).map(bin => (
                              <SelectItem key={bin.storage_bin_id} value={bin.storage_bin_id}>
                                {bin.storage_bin_id} - {bin.name || 'No Name'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {storageBins.filter(b => !b.storage_type_id).length === 0 && (
                          <p className="text-xs text-gray-500 mt-1">All storage bins are already assigned to a type</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="storage_type_id">Storage Type *</Label>
                        <Select value={selectedStorageType} onValueChange={setSelectedStorageType} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageTypes.map(type => (
                              <SelectItem key={type.storage_type_id} value={type.storage_type_id}>
                                {type.storage_type_id} - {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Tab 5: Storage Bin → Storage Section */}
                  {activeTab === 'bin-section' && (
                    <>
                      <div>
                        <Label htmlFor="storage_bin_id">Storage Bin *</Label>
                        <Select value={selectedStorageBin} onValueChange={setSelectedStorageBin} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Bin" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageBins.filter(b => !b.storage_section_id).map(bin => (
                              <SelectItem key={bin.storage_bin_id} value={bin.storage_bin_id}>
                                {bin.storage_bin_id} - {bin.name || 'No Name'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {storageBins.filter(b => !b.storage_section_id).length === 0 && (
                          <p className="text-xs text-gray-500 mt-1">All storage bins are already assigned to a section</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="storage_section_id">Storage Section *</Label>
                        <Select value={selectedStorageSection} onValueChange={setSelectedStorageSection} required>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Storage Section" />
                          </SelectTrigger>
                          <SelectContent>
                            {storageSections.map(section => (
                              <SelectItem key={section.storage_section_id} value={section.storage_section_id}>
                                {section.storage_section_id} - {section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">Create Assignment</Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {activeTab === 'wh-plant-sl' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Warehouse</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plant</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Location</th>
                    </>
                  )}
                  {activeTab === 'type-wh' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Warehouse</th>
                    </>
                  )}
                  {activeTab === 'section-type' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Section</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Type</th>
                    </>
                  )}
                  {activeTab === 'bin-type' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Bin</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Type</th>
                    </>
                  )}
                  {activeTab === 'bin-section' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Bin</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Storage Section</th>
                    </>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentAssignments().length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'wh-plant-sl' ? 4 : 3} className="text-center py-8 text-gray-500">
                      No assignments found.
                    </td>
                  </tr>
                ) : (
                  getCurrentAssignments().map((assignment, index) => {
                    let rowKey = '';
                    if (activeTab === 'wh-plant-sl') {
                      rowKey = `${assignment.warehouse_number}-${assignment.plant_id}-${assignment.storage_location_id}`;
                    } else if (activeTab === 'type-wh') {
                      rowKey = assignment.storage_type_id;
                    } else if (activeTab === 'section-type') {
                      rowKey = assignment.storage_section_id;
                    } else if (activeTab === 'bin-type') {
                      rowKey = `bin-type-${assignment.storage_bin_id}`;
                    } else if (activeTab === 'bin-section') {
                      rowKey = `bin-section-${assignment.storage_bin_id}`;
                    }
                    
                    return (
                    <tr key={rowKey} className="border-b border-gray-100 hover:bg-gray-50">
                      {activeTab === 'wh-plant-sl' && (
                        <>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm font-semibold">{assignment.warehouse_number}</div>
                            <div className="text-xs text-gray-600">{getWarehouseName(assignment.warehouse_number)}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm">{assignment.plant_id}</div>
                            <div className="text-xs text-gray-600">{getPlantName(assignment.plant_id)}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm">{assignment.storage_location_id}</div>
                            <div className="text-xs text-gray-600">
                              {getStorageLocationName(assignment.plant_id, assignment.storage_location_id)}
                            </div>
                          </td>
                        </>
                      )}
                      {activeTab === 'type-wh' && (
                        <>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm font-semibold">{assignment.storage_type_id}</div>
                            <div className="text-xs text-gray-600">{getStorageTypeName(assignment.storage_type_id)}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm">{assignment.warehouse_number || '-'}</div>
                            <div className="text-xs text-gray-600">{assignment.warehouse_number ? getWarehouseName(assignment.warehouse_number) : 'Not assigned'}</div>
                          </td>
                        </>
                      )}
                      {activeTab === 'section-type' && (
                        <>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm font-semibold">{assignment.storage_section_id}</div>
                            <div className="text-xs text-gray-600">{getStorageSectionName(assignment.storage_section_id)}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm">{assignment.storage_type_id || '-'}</div>
                            <div className="text-xs text-gray-600">{assignment.storage_type_id ? getStorageTypeName(assignment.storage_type_id) : 'Not assigned'}</div>
                          </td>
                        </>
                      )}
                      {activeTab === 'bin-type' && (
                        <>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm font-semibold">{assignment.storage_bin_id}</div>
                            <div className="text-xs text-gray-600">{getStorageBinName(assignment.storage_bin_id) || '-'}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm">{assignment.storage_type_id || '-'}</div>
                            <div className="text-xs text-gray-600">{assignment.storage_type_id ? getStorageTypeName(assignment.storage_type_id) : 'Not assigned'}</div>
                          </td>
                        </>
                      )}
                      {activeTab === 'bin-section' && (
                        <>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm font-semibold">{assignment.storage_bin_id}</div>
                            <div className="text-xs text-gray-600">{getStorageBinName(assignment.storage_bin_id) || '-'}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-mono text-sm">{assignment.storage_section_id || '-'}</div>
                            <div className="text-xs text-gray-600">{assignment.storage_section_id ? getStorageSectionName(assignment.storage_section_id) : 'Not assigned'}</div>
                          </td>
                        </>
                      )}
                      <td className="py-3 px-4">
                        {activeTab === 'wh-plant-sl' && (
                        writable && (<Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(assignment)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>)
                        )}
                        {activeTab !== 'wh-plant-sl' && (
                          <span className="text-xs text-gray-500">Via entity form</span>
                        )}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Info Card about other assignments */}
        <Card className="p-6 bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-3">Other EWM Entity Relationships</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              <strong>Warehouse → Storage Type:</strong> Managed via <code className="bg-gray-200 px-1">storage_type.warehouse_number</code> field
            </p>
            <p>
              <strong>Storage Type → Storage Section:</strong> Managed via <code className="bg-gray-200 px-1">storage_section.storage_type_id</code> field
            </p>
            <p>
              <strong>Storage Type → Storage Bin:</strong> Managed via <code className="bg-gray-200 px-1">storage_bin.storage_type_id</code> field
            </p>
            <p>
              <strong>Storage Section → Storage Bin:</strong> Managed via <code className="bg-gray-200 px-1">storage_bin.storage_section_id</code> field
            </p>
            <p className="text-xs text-gray-600 mt-3">
              These relationships are automatically maintained through the entity forms and don't require separate assignment management.
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default EWMAssignments;
